import { datadogRum } from "@datadog/browser-rum";
import type { NextWebVitalsMetric } from "next/app";

const validDatadogEnvironments = [
  "production",
  "preview",
  "development",
  // TODO: 'staging' could be added here if we decided we want to diffentiate between previews and staging canaries (but then we'd also need to add an environment variable of 'staging' for whichever branch we wanted to serve as our canary)
].map((s) => s.toLowerCase());

export const isValidDatadogEnvironment = () => {
  const datadogEnv = process.env.NEXT_PUBLIC_APP_ENV;
  return (
    datadogEnv && validDatadogEnvironments.includes(datadogEnv.toLowerCase())
  );
};

export function initializeDatadogRum() {
  // Don't initialize during SSR
  if (typeof window === "undefined") {
    // eslint-disable-next-line no-console
    console.info("Datadog RUM not initialized: not running in browser.");
    return;
  }

  // @ts-ignore
  if (window._DATADOG_SYNTHETICS_BROWSER !== undefined) {
    // eslint-disable-next-line no-console
    console.info("Datadog RUM not initialized: running in Datadog Synthetics.");
    return;
  }

  // @ts-ignore
  if (window._PLAYWRIGHT_BROWSER !== undefined) {
    // eslint-disable-next-line no-console
    console.info("Datadog RUM not initialized: running in Playwright browser.");
    return;
  }

  if (!isValidDatadogEnvironment()) {
    // eslint-disable-next-line no-console
    console.info(
      `Datadog RUM not initialized: cannot identify the environment (value provided was "${process.env.NEXT_PUBLIC_APP_ENV}").`,
    );
    return;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) {
    // eslint-disable-next-line no-console
    console.info(
      "Datadog RUM not initialized: missing applicationId or clientToken.",
    );
    return;
  }

  // See https://docs.datadoghq.com/real_user_monitoring/installation/?tab=us#initialization-parameters
  // for more information on what these individual parameters do.
  datadogRum.init({
    applicationId,
    clientToken,
    traceSampleRate: 10,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
    env: process.env.NEXT_PUBLIC_APP_ENV,
    version: process.env.NEXT_PUBLIC_COMMIT_SHA,
    // Enables distributed tracing for any `*.zapier.com` or `*.zapier-staging.com` APIs.
    // See: https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
    allowedTracingUrls: [
      // zapier.com, with or without a subdomain
      /https:\/\/zapier\.com/,
      /https:\/\/.*\.zapier\.com/,
      // zapier-staging.com, with or without a subdomain
      /https:\/\/zapier-staging\.com/,
      /https:\/\/.*\.zapier-staging\.com/,
      // zapier-deployment.com, with or without a subdomain
      /https:\/\/zapier-deployment\.com/,
      /https:\/\/.*\.zapier-deployment\.com/,
    ],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    useCrossSiteSessionCookie: true,
  });

  // eslint-disable-next-line no-console
  console.info(`Datadog RUM has been initialized.`);
}

const vitalsMapper: { [key: string]: string } = {
  // Length of time it takes for the page to start and finish hydrating (in ms)
  "Next.js-hydration": "nextJsHydration",
  // Length of time it takes for a page to start rendering after a route change (in ms)
  "Next.js-route-change-to-render": "nextJsRouteChangeToRender",
  // Length of time it takes for a page to finish render after a route change (in ms)
  "Next.js-render": "nextJsRender",

  // Web Vitals - https://web.dev/vitals/
  CLS: "cumulativeLayoutShift",
  FCP: "firstContentfulPaint",
  FID: "firstInputDelay",
  INP: "interactionToNextPaint",
  LCP: "largestContentfulPaint",
  TTFB: "timeToFirstByte",
};

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export function nextJsReportWebVitals(metric: NextWebVitalsMetric) {
  const mappedName = vitalsMapper[metric.name] || metric.name;

  datadogRum.addAction(`Web Vital: ${mappedName}`, {
    [mappedName]: metric.value,
  });
}
